/* Call recordings list with playback, AI transcript + auto-summary.
 *
 * Each row shows:
 *  • Audio player (proxy-streamed via backend so Twilio creds stay server-side)
 *  • Status pill: pending → transcribing → summarizing → done | failed
 *  • Expandable AI summary card (TL;DR, sentiment, asking price, next step,
 *    key quotes, objections) sourced from Claude Sonnet 4.6
 *  • Full transcript collapsible
 *  • Retry button if status is failed
 *
 * While any recording is still in-flight (pending|transcribing|summarizing),
 * we poll every 4s for up to 5 minutes. As soon as everything is done, we
 * stop polling.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  Mic, Loader2, Trash2, PhoneIncoming, PhoneOutgoing, ChevronDown,
  Sparkles, MessageSquareText, AlertCircle, RefreshCw, FileText,
  Flame, CheckCircle2, Trophy, Target, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 4000;
const POLL_MAX_MS = 5 * 60 * 1000;

function fmtDur(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

const SENTIMENT_STYLE = {
  motivated:  { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: Flame, label: "Motivated" },
  interested: { cls: "bg-sky-100 text-sky-800 border-sky-200", icon: CheckCircle2, label: "Interested" },
  neutral:    { cls: "bg-slate-100 text-slate-700 border-slate-200", icon: MessageSquareText, label: "Neutral" },
  cold:       { cls: "bg-blue-100 text-blue-800 border-blue-200", icon: MessageSquareText, label: "Cold" },
  hostile:    { cls: "bg-red-100 text-red-800 border-red-200", icon: AlertCircle, label: "Hostile" },
};

const STATUS_PILL = {
  pending:      { cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Queued",         spin: true },
  transcribing: { cls: "bg-indigo-100 text-indigo-700 border-indigo-200", label: "Transcribing…", spin: true },
  summarizing:  { cls: "bg-purple-100 text-purple-700 border-purple-200", label: "Summarizing…",  spin: true },
  done:         { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "AI ready",   spin: false },
  failed:       { cls: "bg-red-100 text-red-700 border-red-200", label: "AI failed",  spin: false },
  skipped_tier: { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "AI ready to run", spin: false },
};

export default function CallRecordingsList({ leadId, buyerId, refreshTrigger }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // recordingId → true

  const load = useCallback(async () => {
    try {
      const params = {};
      if (leadId) params.lead_id = leadId;
      if (buyerId) params.buyer_id = buyerId;
      const { data } = await api.get("/voice/recordings", { params });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [leadId, buyerId]);

  // Initial + manual-refresh load.
  useEffect(() => { load(); }, [load, refreshTrigger]);

  // Polling: while any recording is still being processed, refetch every
  // POLL_MS for up to POLL_MAX_MS. Items list is intentionally NOT in deps —
  // we always re-evaluate inside the interval callback to avoid restart loops.
  const inFlight = items.some((r) =>
    ["pending", "transcribing", "summarizing"].includes(r.intel_status));
  useEffect(() => {
    if (!inFlight) return undefined;
    const startedAt = Date.now();
    const id = setInterval(() => {
      if (Date.now() - startedAt > POLL_MAX_MS) {
        clearInterval(id);
        return;
      }
      load();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [inFlight, load]);

  const onDelete = async (rec) => {
    if (!window.confirm("Delete this recording from Twilio? This cannot be undone.")) return;
    try {
      await api.delete(`/voice/recordings/${rec.id}`);
      setItems((prev) => prev.filter((r) => r.id !== rec.id));
      toast.success("Recording deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onRetry = async (rec) => {
    try {
      await api.post(`/voice/recordings/${rec.id}/transcribe`);
      setItems((prev) => prev.map((r) =>
        r.id === rec.id ? { ...r, intel_status: "pending", intel_error: null } : r));
      toast.success("Queued — AI summary will appear shortly");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not requeue");
    }
  };

  const audioUrl = (rec) => `${BACKEND_URL}/api/voice/recordings/${rec.id}/audio`;
  const toggle = (id) => setExpanded((m) => ({ ...m, [id]: !m[id] }));

  if (loading) {
    return (
      <div className="text-center text-[var(--ink-2)] text-sm py-6" data-testid="recordings-loading">
        <Loader2 size={14} className="animate-spin inline mr-2" />
        Loading call recordings…
      </div>
    );
  }
  if (!items.length) {
    return (
      <div
        className="text-center text-[var(--ink-2)] text-sm py-6 border border-dashed border-[var(--line)] rounded-lg"
        data-testid="recordings-empty"
      >
        <Mic size={18} className="inline mb-1 opacity-50" />
        <div>No call recordings yet.</div>
        <div className="text-[11px] mt-1 opacity-75">
          Use the phone icon next to a number to start a recorded call.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="recordings-list">
      {items.map((rec) => {
        const isExpanded = !!expanded[rec.id];
        const statusKey = rec.intel_status || "pending";
        const pill = STATUS_PILL[statusKey] || STATUS_PILL.pending;
        const summary = rec.summary || {};
        const sent = SENTIMENT_STYLE[summary.sentiment] || SENTIMENT_STYLE.neutral;
        const SentIcon = sent.icon;

        return (
          <div
            key={rec.id}
            className="bg-white border border-[var(--line)] rounded-lg overflow-hidden"
            data-testid={`recording-row-${rec.id}`}
          >
            {/* HEADER ROW */}
            <div className="p-3 flex items-center gap-3">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                  rec.direction === "inbound"
                    ? "bg-sky-50 text-sky-700 border border-sky-200"
                    : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                }`}
                title={rec.direction === "inbound" ? "Inbound" : "Outbound"}
              >
                {rec.direction === "inbound"
                  ? <PhoneIncoming size={14} />
                  : <PhoneOutgoing size={14} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs text-[var(--ink-2)] flex-wrap">
                  <span className="font-mono">
                    {rec.direction === "inbound" ? rec.from_number : rec.to_number}
                  </span>
                  <span>•</span>
                  <span>{fmtDur(rec.duration_seconds || 0)}</span>
                  <span>•</span>
                  <span>{timeAgo(rec.created_at)}</span>
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${pill.cls}`}
                    data-testid={`recording-status-${rec.id}`}
                  >
                    {pill.spin && <Loader2 size={9} className="animate-spin" />}
                    {pill.label}
                  </span>
                </div>
                <audio
                  src={audioUrl(rec)}
                  controls
                  preload="none"
                  className="w-full h-8 mt-1.5"
                  data-testid={`recording-audio-${rec.id}`}
                />
              </div>
              <button
                type="button"
                onClick={() => onDelete(rec)}
                className="w-7 h-7 rounded-md hover:bg-red-50 text-red-500 flex items-center justify-center transition shrink-0"
                title="Delete recording"
                data-testid={`recording-delete-${rec.id}`}
              >
                <Trash2 size={13} />
              </button>
            </div>

            {/* AI INTEL SECTION */}
            {statusKey === "done" && summary.tldr && (
              <div className="border-t border-[var(--line)] bg-gradient-to-br from-indigo-50/50 to-purple-50/30 px-3 py-2.5">
                <div className="flex items-start gap-2 mb-2">
                  <Sparkles size={13} className="text-indigo-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-700">
                        AI Summary
                      </div>
                      {rec.coach_score != null && (
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${
                            rec.coach_score >= 85 ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                            rec.coach_score >= 70 ? "bg-sky-50 text-sky-700 border-sky-200" :
                            rec.coach_score >= 50 ? "bg-amber-50 text-amber-700 border-amber-200" :
                            "bg-red-50 text-red-700 border-red-200"
                          }`}
                          data-testid={`recording-coach-score-${rec.id}`}
                          title="AI Coach score for this call"
                        >
                          <Trophy size={9} /> Coach {rec.coach_score}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[var(--ink)] leading-snug" data-testid={`recording-tldr-${rec.id}`}>
                      {summary.tldr}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {summary.sentiment && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${sent.cls}`}>
                      <SentIcon size={10} /> {sent.label}
                    </span>
                  )}
                  {summary.asking_price && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-amber-200 bg-amber-50 text-amber-900">
                      Ask: {summary.asking_price}
                    </span>
                  )}
                  {summary.buyer_budget && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-violet-200 bg-violet-50 text-violet-900">
                      Budget: {summary.buyer_budget}
                    </span>
                  )}
                  {summary.follow_up_at && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-blue-200 bg-blue-50 text-blue-900">
                      Follow up: {summary.follow_up_at}
                    </span>
                  )}
                </div>
                {summary.next_step && (
                  <div className="text-xs flex items-start gap-1.5 mb-1.5 bg-white/70 border border-indigo-100 rounded-md px-2 py-1.5" data-testid={`recording-next-step-${rec.id}`}>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-indigo-700 shrink-0 mt-0.5">Next →</span>
                    <span className="text-[var(--ink)]">{summary.next_step}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => toggle(rec.id)}
                  className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 flex items-center gap-1 mt-1"
                  data-testid={`recording-expand-${rec.id}`}
                >
                  {isExpanded ? "Hide details" : "Show transcript & quotes"}
                  <ChevronDown size={11} className={`transition ${isExpanded ? "rotate-180" : ""}`} />
                </button>
                {isExpanded && (
                  <div className="mt-2 space-y-2.5 pl-3 border-l-2 border-indigo-200" data-testid={`recording-detail-${rec.id}`}>
                    {rec.coach && (rec.coach.coach_note || rec.coach.score != null) && (
                      <div className="bg-white border border-indigo-100 rounded-md p-2.5" data-testid={`recording-coach-${rec.id}`}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Trophy size={11} className="text-amber-500" />
                          <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-700">
                            AI Coach feedback
                          </div>
                        </div>
                        {rec.coach.coach_note && (
                          <div className="text-xs text-[var(--ink)] leading-snug mb-2 italic">
                            {rec.coach.coach_note}
                          </div>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-2">
                          {rec.coach.talk_ratio != null && (
                            <div className="text-[10px]">
                              <div className="text-[var(--ink-3)] font-bold uppercase tracking-wider">Talk ratio</div>
                              <div className="font-mono font-bold text-[var(--ink)]">{Math.round(rec.coach.talk_ratio * 100)}%</div>
                            </div>
                          )}
                          {rec.coach.rapport && (
                            <div className="text-[10px]">
                              <div className="text-[var(--ink-3)] font-bold uppercase tracking-wider">Rapport</div>
                              <div className="font-bold capitalize text-[var(--ink)]">{rec.coach.rapport}</div>
                            </div>
                          )}
                          {rec.coach.discovery && (
                            <div className="text-[10px]">
                              <div className="text-[var(--ink-3)] font-bold uppercase tracking-wider">Discovery</div>
                              <div className="font-bold capitalize text-[var(--ink)]">{rec.coach.discovery}</div>
                            </div>
                          )}
                          {rec.coach.objection_handling && (
                            <div className="text-[10px]">
                              <div className="text-[var(--ink-3)] font-bold uppercase tracking-wider">Objections</div>
                              <div className="font-bold capitalize text-[var(--ink)]">{rec.coach.objection_handling}</div>
                            </div>
                          )}
                        </div>
                        {(rec.coach.strengths || []).length > 0 && (
                          <div className="mb-1.5">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-0.5 flex items-center gap-1">
                              <TrendingUp size={9} /> Strengths
                            </div>
                            <ul className="space-y-0.5 list-disc list-inside text-xs text-[var(--ink-2)]">
                              {rec.coach.strengths.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        {(rec.coach.improvements || []).length > 0 && (
                          <div className="mb-1.5">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-0.5 flex items-center gap-1">
                              <Target size={9} /> Do better
                            </div>
                            <ul className="space-y-0.5 list-disc list-inside text-xs text-[var(--ink-2)]">
                              {rec.coach.improvements.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        {(rec.coach.missed_opportunities || []).length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700 mb-0.5 flex items-center gap-1">
                              <AlertCircle size={9} /> Missed opportunities
                            </div>
                            <ul className="space-y-0.5 list-disc list-inside text-xs text-[var(--ink-2)]">
                              {rec.coach.missed_opportunities.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    {(summary.key_quotes || []).length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-700 mb-1">Key quotes</div>
                        <ul className="space-y-1">
                          {summary.key_quotes.map((q, i) => (
                            <li key={i} className="text-xs text-[var(--ink-2)] italic">&ldquo;{q}&rdquo;</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(summary.objections || []).length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700 mb-1">Objections</div>
                        <ul className="space-y-0.5 list-disc list-inside">
                          {summary.objections.map((o, i) => (
                            <li key={i} className="text-xs text-[var(--ink-2)]">{o}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {rec.transcript && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--ink-3)] mb-1 flex items-center gap-1">
                          <FileText size={10} /> Full transcript
                        </div>
                        <div className="text-xs text-[var(--ink-2)] whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto bg-white border border-[var(--line)] rounded p-2">
                          {rec.transcript}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {statusKey === "skipped_tier" && (
              <div className="border-t border-[var(--line)] bg-amber-50/60 px-3 py-2 flex items-center gap-2 text-xs text-amber-900">
                <Sparkles size={12} className="shrink-0 text-amber-600" />
                <span className="flex-1">Get an AI summary + coach score for this call.</span>
                <button
                  type="button"
                  onClick={() => onRetry(rec)}
                  data-testid={`recording-run-ai-${rec.id}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white font-bold text-[11px] transition"
                >
                  <Sparkles size={10} /> Run AI Summary
                </button>
              </div>
            )}

            {statusKey === "failed" && (
              <div className="border-t border-[var(--line)] bg-red-50/50 px-3 py-2 flex items-center gap-2 text-xs text-red-800">
                <AlertCircle size={12} className="shrink-0" />
                <span className="flex-1 truncate" title={rec.intel_error}>
                  {rec.intel_error || "Transcription failed."}
                </span>
                <button
                  type="button"
                  onClick={() => onRetry(rec)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-red-200 hover:bg-red-100 text-red-700 font-bold text-[11px] transition"
                  data-testid={`recording-retry-${rec.id}`}
                >
                  <RefreshCw size={10} /> Retry
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
